/**
 * Deterministic, DOM-free v292 settlement model.  The immutable legacy HTML is
 * the source of truth; values below were transcribed from its v292 tables.
 * Presentation-only suspense/revival/cue animation is deliberately excluded.
 */
import { API_VERSION, GAME_CORE_VERSION, GAME_ID, LEGACY_VERSION } from "./version.generated.js";
export { API_VERSION, GAME_CORE_VERSION, GAME_ID, LEGACY_VERSION };
/** This remains false until server replay is wired for every legacy path. */
export const PRODUCTION_ENABLED = false;
export const COINS = [1, 2, 5, 10];
export const GARDENS = [1, 2, 3, 4];
const SYMBOLS = Object.freeze({
    seven: { weight: 1, payout: 300 }, bar: { weight: 4, payout: 80 }, bell: { weight: 6, payout: 50 }, grape: { weight: 12, payout: 14 }, plum: { weight: 17, payout: 9 }, orange: { weight: 21, payout: 6 }, cherry: { weight: 24, payout: 4 }, blank: { weight: 15 },
    flowerLine: { weight: 0, payout: 300, wild: true }, ivory: { weight: 0, payout: 300, wild: true }, crimson: { weight: 0, payout: 300, wild: true }, golden: { weight: 0, payout: 300, wild: true }, azure: { weight: 0, payout: 300, wild: true }, mysticBud: { weight: 0 },
});
const BASE_KEYS = ["seven", "bar", "bell", "grape", "plum", "orange", "cherry", "blank"];
const NORMAL_KEYS = ["seven", "bar", "bell", "grape", "plum", "orange", "cherry"];
const ANY_PAYTABLE = Object.freeze({
    cherry: { 4: 1, 5: 2, 6: 5, 7: 12, 8: 35, 9: 150 }, orange: { 4: 1, 5: 3, 6: 7, 7: 18, 8: 50, 9: 220 }, plum: { 4: 2, 5: 4, 6: 10, 7: 25, 8: 70, 9: 320 }, grape: { 4: 3, 5: 6, 6: 15, 7: 38, 8: 110, 9: 500 }, bell: { 3: 1, 4: 5, 5: 12, 6: 30, 7: 80, 8: 220, 9: 800 }, bar: { 3: 1, 4: 8, 5: 20, 6: 50, 7: 140, 8: 400, 9: 1500 }, seven: { 3: 1, 4: 15, 5: 50, 6: 150, 7: 400, 8: 1000, 9: 3000 },
});
const GROUPS = [
    [[0, 0], [1, 0], [2, 0]], [[0, 1], [1, 1], [2, 1]], [[0, 2], [1, 2], [2, 2]], [[0, 0], [0, 1], [0, 2]], [[1, 0], [1, 1], [1, 2]], [[2, 0], [2, 1], [2, 2]], [[0, 0], [1, 1], [2, 2]], [[0, 2], [1, 1], [2, 0]],
    [[0, 0], [1, 0], [2, 1]], [[0, 1], [1, 0], [2, 0]], [[0, 1], [1, 1], [2, 0]], [[0, 2], [1, 1], [2, 1]], [[0, 0], [1, 1], [2, 1]], [[0, 1], [1, 1], [2, 2]], [[0, 1], [1, 2], [2, 2]], [[0, 2], [1, 2], [2, 1]],
    [[0, 0], [0, 1], [1, 2]], [[1, 0], [2, 1], [2, 2]], [[2, 0], [1, 1], [1, 2]], [[1, 0], [1, 1], [0, 2]], [[1, 0], [0, 1], [0, 2]], [[0, 0], [1, 1], [1, 2]], [[1, 0], [1, 1], [2, 2]], [[2, 0], [2, 1], [1, 2]],
    [[0, 1], [1, 0], [2, 1]], [[0, 0], [1, 1], [0, 2]], [[0, 0], [1, 1], [2, 0]], [[0, 1], [1, 2], [2, 1]], [[1, 0], [2, 1], [1, 2]], [[1, 0], [0, 1], [1, 2]], [[0, 2], [1, 1], [2, 2]], [[2, 0], [1, 1], [2, 2]],
];
/** Exported for table parity fixtures and release review. */
export const V292_TABLES = Object.freeze({
    baseWeights: Object.freeze(Object.fromEntries(BASE_KEYS.map(key => [key, SYMBOLS[key].weight]))),
    normalBudTarget: .095,
    normalBudWithoutBlueCue: .07012173913043479,
    blueCueChance: Object.freeze({ normal: .05, bonus: .20 }),
    blueCue: Object.freeze([{ key: "cherry", weight: 24, budChance: .20 }, { key: "orange", weight: 22, budChance: .30 }, { key: "plum", weight: 18, budChance: .40 }, { key: "grape", weight: 15, budChance: .52 }, { key: "bar", weight: 10, budChance: .66 }, { key: "bell", weight: 7, budChance: .82 }, { key: "seven", weight: 4, budChance: 1 }]),
    freeGrowthWeights: Object.freeze([{ level: 1, weight: .35 }, { level: 2, weight: .40 }, { level: 3, weight: .20 }, { level: 4, weight: .05 }]),
    freeBudRates: Object.freeze({ 1: Object.freeze([.34, .30, .25, .20, .145, .09, .05, .016]), 2: Object.freeze([.52, .35, 7 / 24, 7 / 30, 169 / 1000, .105, 7 / 120, 7 / 375]), 3: Object.freeze([.72, .40, 1 / 3, 4 / 15, 29 / 150, .12, 1 / 15, 8 / 375]), 4: Object.freeze([.88, .45, .375, .30, .2175, .135, .075, .024]) }),
    overlapChance: .095,
    bloomChance: Object.freeze({ normal: .25, free1: .25, free2: .25, free3: .33, free4: .33 }),
    specialBudRate: .45,
    specialChance: Object.freeze({ modeChanceAtLevel3Plus: .20, threeTargetChance: .20, initialTierWeights: Object.freeze({ white: .80, red: .15, gold: .05 }), firstHitPromotionChance: .50 }),
    /** Final v321 BLUE Mystery override; it supersedes the older v254 level table and the v291
     * override that first fixed the event rate/ordinal ladder used below. v321 keeps both of
     * those exactly, and additionally fixes the terminal (last free cell) draw at 25% -- v404
     * documents this as a deliberate departure from the pre-v321 100%-certain terminal bloom
     * ("the final lone square is truly 25%, not silently 100%"). */
    blueMystery: Object.freeze({ eventRate: .45, finalMultiplierAtReverseSpin: 10, terminalBloomChance: .25, successByBudOrdinal: Object.freeze({ firstTwo: .25, nextTwo: 1 / 3, fifthPlusBase: .50, fifthPlusStep: .10, fifthPlusMax: .90 }) }),
});
export const DOUBLE_REEL_SYMBOLS = Object.freeze([
    { key: "blank", rank: 0, bonus: 0, weight: 28, matchGate: .38, blank: true }, { key: "cherry", rank: 1, bonus: 1, weight: 22, matchGate: .16 }, { key: "orange", rank: 2, bonus: 1, weight: 17, matchGate: .20 }, { key: "plum", rank: 3, bonus: 2, weight: 12, matchGate: .28 }, { key: "grape", rank: 4, bonus: 3, weight: 8, matchGate: .36 }, { key: "bell", rank: 5, bonus: 5, weight: 5.5, matchGate: .46 }, { key: "bar", rank: 6, bonus: 8, weight: 3.5, matchGate: .58 }, { key: "seven", rank: 7, bonus: 15, weight: 2.3, matchGate: .72 }, { key: "flowerLine", rank: 8, bonus: 30, weight: 1.7, matchGate: .86, wild: true },
]);
export const INITIAL_CORE_STATE = () => ({ revision: 0, freeSpinsRemaining: 0, inBonus: false, freeGrowthLevel: 0, specialFree: false, specialBudStops: 0, specialChanceTargets: [], specialFirstHitPromotionDone: false, specialRefillCount: 0, specialRefillDone: false, sticky: [], pendingWin: 0n, bonusWin: 0n, doublePot: 0n, doubleHold: 0n, blueMystery: { phase: "idle", consumed: false, reverseSpins: 0, budStops: 0, finalMultiplier: 0 } });
export function assertValidAction(action) { if (!COINS.includes(action.coin) || !GARDENS.includes(action.garden))
    throw new Error("coin/garden is not allowed"); }
export function currentBet(action) { assertValidAction(action); return BigInt(8 * action.garden * action.coin); }
function roll(rng) { const value = rng.next(); if (!Number.isFinite(value) || value < 0 || value >= 1)
    throw new Error("RNG must return [0, 1)"); return value; }
function cloneState(state) { return { ...state, sticky: state.sticky.map(item => ({ ...item, cell: [item.cell[0], item.cell[1]] })), specialChanceTargets: state.specialChanceTargets.map(item => ({ ...item, cell: [item.cell[0], item.cell[1]] })), blueMystery: { ...state.blueMystery } }; }
function assertCoreStateInvariant(state) {
    if (!state.specialFree && state.specialChanceTargets.length)
        throw new Error("CHANCE targets require Special FREE");
    const targetCells = new Set();
    for (const target of state.specialChanceTargets) {
        const key = `${target.cell[0]},${target.cell[1]}`;
        if (!Number.isInteger(target.cell[0]) || !Number.isInteger(target.cell[1]) || target.cell[0] < 0 || target.cell[0] > 2 || target.cell[1] < 0 || target.cell[1] > 2 || targetCells.has(key))
            throw new Error("invalid CHANCE target state");
        targetCells.add(key);
    }
    if (state.sticky.some((item) => targetCells.has(`${item.cell[0]},${item.cell[1]}`)))
        throw new Error("CHANCE target overlaps sticky symbol");
}
function isWild(key) { return !!SYMBOLS[key].wild; }
function clamp01(value) { return Math.max(0, Math.min(1, value)); }
function pickWeighted(items, rng) { let remainder = roll(rng) * items.reduce((sum, item) => sum + item.weight, 0); for (const item of items) {
    remainder -= item.weight;
    if (remainder <= 0)
        return item;
} return items[items.length - 1]; }
/** v292 base picker uses a strict cumulative boundary (`r < sum`). */
export function weightedPick(rng) { let total = 0; const cumulative = BASE_KEYS.map(key => [key, total += SYMBOLS[key].weight]); const value = roll(rng) * total; return cumulative.find(([, sum]) => value < sum)?.[0] ?? "cherry"; }
export function evaluateGrid(grid, action) {
    const lines = [];
    const coin = BigInt(action.coin);
    GROUPS.slice(0, action.garden * 8).forEach((cells, index) => { const keys = cells.map(([c, r]) => grid[c][r]); if (keys.some(key => SYMBOLS[key].block))
        return; const target = keys.find(key => !isWild(key)) ?? "flowerLine"; const payout = SYMBOLS[target].payout; if (payout && keys.every(key => key === target || isWild(key)))
        lines.push({ rule: `LINE_${index + 1}_${target}`, amount: BigInt(payout) * coin, cells }); });
    for (const symbol of NORMAL_KEYS) {
        const naturals = [];
        const wilds = [];
        for (let c = 0; c < 3; c++)
            for (let r = 0; r < 3; r++) {
                const key = grid[c][r];
                if (key === symbol)
                    naturals.push([c, r]);
                else if (isWild(key))
                    wilds.push([c, r]);
            }
        const count = naturals.length + wilds.length;
        const multiple = ANY_PAYTABLE[symbol][Math.min(9, count)] ?? 0;
        if (multiple)
            lines.push({ rule: `ANY_${symbol}_${count}`, amount: BigInt(multiple) * currentBet(action), cells: [...naturals, ...wilds] });
    }
    return lines;
}
function chooseBloom(rng) { const value = roll(rng); return value < .80 ? "ivory" : value < .98 ? "crimson" : "golden"; }
function bloomSpins(symbol) { return symbol === "ivory" ? 2 : symbol === "crimson" ? 3 : symbol === "golden" ? 5 : 0; }
function chooseFreeGrowthLevel(rng) { return pickWeighted(V292_TABLES.freeGrowthWeights, rng).level; }
function freeBudRate(level, stickyCount) { if (stickyCount >= 9)
    return 0; const row = V292_TABLES.freeBudRates[level] ?? V292_TABLES.freeBudRates[2]; return row[Math.max(0, Math.min(7, Math.max(1, stickyCount | 0) - 1))]; }
function blueCueChance(target, eligible, isBonus) {
    if (!eligible.length || target <= 0)
        return 0;
    if (!isBonus)
        return V292_TABLES.blueCueChance.normal;
    const table = V292_TABLES.blueCue.filter(item => eligible.includes(item.key));
    const expectation = table.reduce((sum, item) => sum + item.weight * item.budChance, 0) / table.reduce((sum, item) => sum + item.weight, 0);
    if (expectation <= 0)
        return 0;
    const relative = target <= .55 ? target / .55 : (1 - target) / .45;
    return clamp01(Math.min(.20 * clamp01(relative), target / expectation, (1 - target) / Math.max(1e-12, 1 - expectation)));
}
function chooseBlueCue(eligible, rng) { const rows = V292_TABLES.blueCue.filter(item => eligible.includes(item.key)); return pickWeighted(rows, rng); }
function nonCueRate(target, cueChance, eligible) { if (!eligible.length || cueChance === 0)
    return target; const rows = V292_TABLES.blueCue.filter(item => eligible.includes(item.key)); const expectation = rows.reduce((sum, item) => sum + item.weight * item.budChance, 0) / rows.reduce((sum, item) => sum + item.weight, 0); return clamp01((target - cueChance * expectation) / (1 - cueChance)); }
function shuffle(input, rng) { const copy = [...input]; for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(roll(rng) * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
} return copy; }
function specialBloomChance(ordinal, freeCells) { if (freeCells === 1)
    return 1; if (ordinal <= 2)
    return .25; if (ordinal <= 4)
    return 1 / 3; return Math.min(.90, .50 + .10 * (ordinal - 5)); }
function nextSpecialChanceTier(tier) { return tier === "white" ? "red" : tier === "red" ? "gold" : "purple"; }
function pickSpecialChanceTier(rng) { const value = roll(rng); return value < .80 ? "white" : value < .95 ? "red" : "gold"; }
function chooseSpecialChanceBloom(tier, rng) {
    if (tier === "purple")
        return "azure";
    if (tier === "gold")
        return "golden";
    if (tier === "red")
        return roll(rng) < .90 ? "crimson" : "golden";
    return chooseBloom(rng);
}
function specialTargetAt(state, cell) { return state.specialChanceTargets.find(item => item.cell[0] === cell[0] && item.cell[1] === cell[1]); }
function selectSpecialChanceTargets(state, rng, cues) {
    const occupied = new Set(state.sticky.map(item => `${item.cell[0]},${item.cell[1]}`));
    const available = [];
    for (let c = 0; c < 3; c++)
        for (let r = 0; r < 3; r++)
            if (!occupied.has(`${c},${r}`))
                available.push([c, r]);
    if (available.length < 2) {
        state.specialFree = false;
        return;
    }
    const count = available.length >= 3 && roll(rng) < V292_TABLES.specialChance.threeTargetChance ? 3 : 2;
    state.specialChanceTargets = shuffle(available, rng).slice(0, count).map(cell => ({ cell, tier: pickSpecialChanceTier(rng) }));
    state.specialFirstHitPromotionDone = false;
    cues.push(`chance:targets:${count}`, ...state.specialChanceTargets.map(target => `chance:target:${target.cell[0]},${target.cell[1]}:${target.tier}`));
}
function consumeSpecialChanceTarget(state, target, rng, cues) {
    state.specialChanceTargets = state.specialChanceTargets.filter(item => item !== target);
    cues.push(`chance:hit:${target.cell[0]},${target.cell[1]}:${target.tier}`);
    if (state.specialFirstHitPromotionDone)
        return;
    state.specialFirstHitPromotionDone = true;
    for (const remaining of state.specialChanceTargets) {
        if (roll(rng) < V292_TABLES.specialChance.firstHitPromotionChance) {
            const previous = remaining.tier;
            remaining.tier = nextSpecialChanceTier(previous);
            cues.push(`chance:promote:${remaining.cell[0]},${remaining.cell[1]}:${previous}->${remaining.tier}`);
        }
    }
}
/** v301: the refill cell's tier shifts the initial white/red/gold weights up one rank (no white). */
function rollSpecialChanceRefillTier(rng) {
    const value = roll(rng);
    const weights = V292_TABLES.specialChance.initialTierWeights;
    if (value < weights.white)
        return "red";
    if (value < weights.white + weights.red)
        return "gold";
    return "purple";
}
/** v301: once every current CHANCE target is cleared, the first refill is guaranteed and every
 * later one is a fresh 50% continuation check; the chain ends on a miss or when no cell is free. */
function maybeRefillSpecialChanceTarget(state, rng, cues) {
    if (!state.specialFree || state.specialChanceTargets.length || state.specialRefillDone)
        return;
    const guaranteed = state.specialRefillCount === 0;
    if (!guaranteed && roll(rng) >= .50) {
        state.specialRefillDone = true;
        cues.push("chance:refill_chain_end:miss");
        return;
    }
    const occupied = new Set(state.sticky.map(item => `${item.cell[0]},${item.cell[1]}`));
    const available = [];
    for (let c = 0; c < 3; c++)
        for (let r = 0; r < 3; r++)
            if (!occupied.has(`${c},${r}`))
                available.push([c, r]);
    if (!available.length) {
        state.specialRefillDone = true;
        cues.push("chance:refill_chain_end:no_cell");
        return;
    }
    const cell = shuffle(available, rng)[0];
    const tier = rollSpecialChanceRefillTier(rng);
    state.specialChanceTargets = [{ cell, tier }];
    state.specialRefillCount++;
    cues.push(`chance:refill:${cell[0]},${cell[1]}:${tier}`, `chance:refill_count:${state.specialRefillCount}`);
}
/** Final v291 BLUE Mystery probability ladder, counted by stopped buds rather than spins. */
export function blueMysteryBloomChance(ordinal, freeCells) {
    if (freeCells === 1)
        return V292_TABLES.blueMystery.terminalBloomChance;
    if (ordinal <= 2)
        return V292_TABLES.blueMystery.successByBudOrdinal.firstTwo;
    if (ordinal <= 4)
        return V292_TABLES.blueMystery.successByBudOrdinal.nextTwo;
    return Math.min(V292_TABLES.blueMystery.successByBudOrdinal.fifthPlusMax, V292_TABLES.blueMystery.successByBudOrdinal.fifthPlusBase + V292_TABLES.blueMystery.successByBudOrdinal.fifthPlusStep * (ordinal - 5));
}
function hasAzureSticky(state) { return state.sticky.some((item) => item.symbol === "azure"); }
function startBlueMystery(state, cues) {
    // v198/v199 entry keeps only the Azure trigger flower; ordinary FREE wilds do not cross the seam.
    state.sticky = state.sticky.filter((item) => item.symbol === "azure");
    state.blueMystery = { phase: "collecting", consumed: true, reverseSpins: 0, budStops: 0, finalMultiplier: 0 };
    state.freeGrowthLevel = 1;
    state.specialFree = false;
    state.specialChanceTargets = [];
    state.specialFirstHitPromotionDone = false;
    state.specialRefillCount = 0;
    state.specialRefillDone = false;
    cues.push("blue_mystery:entry", "blue_mystery:prune_to_azure");
}
function handBackBlueMystery(state, rng, cues) {
    state.blueMystery.phase = "spent";
    // v207 rerolls ordinary FREE level after the synchronized Azure conversion.
    state.freeGrowthLevel = chooseFreeGrowthLevel(rng);
    state.specialFree = false;
    state.specialChanceTargets = [];
    state.specialFirstHitPromotionDone = false;
    state.specialRefillCount = 0;
    state.specialRefillDone = false;
    cues.push("blue_mystery:released", `blue_mystery:handback:${state.freeGrowthLevel}`);
}
/**
 * Settlement order follows v292 generateFinalGrid -> resolveBloomPlan -> evaluate
 * -> processBlooms: only generated cells use RNG; sticky wilds persist through FREE.
 */
export function resolveSpin(state, action, rng) {
    assertValidAction(action);
    assertCoreStateInvariant(state);
    const nextState = cloneState(state);
    const isBlueCollecting = nextState.inBonus && nextState.blueMystery.phase === "collecting";
    const isMysticFinal = nextState.inBonus && nextState.blueMystery.phase === "final-multiplier";
    const isFreeSpin = (nextState.inBonus && nextState.freeSpinsRemaining > 0) || isBlueCollecting || isMysticFinal;
    const sticky = new Map(nextState.sticky.map(item => [`${item.cell[0]},${item.cell[1]}`, item]));
    const grid = Array.from({ length: 3 }, () => Array(3).fill("blank"));
    const available = [];
    for (let c = 0; c < 3; c++)
        for (let r = 0; r < 3; r++) {
            const held = nextState.inBonus ? sticky.get(`${c},${r}`) : undefined;
            grid[c][r] = held?.symbol ?? weightedPick(rng);
            if (!held)
                available.push([c, r]);
        }
    const cues = [];
    const blooms = [];
    const isSpecial = nextState.inBonus && nextState.specialFree;
    let blueSucceeded = false;
    let blueReleaseCells = [];
    if (isBlueCollecting) {
        // v291: each reverse spin is accumulated, gets one direct 45% bud event, and never has a blue cue.
        nextState.blueMystery.reverseSpins++;
        nextState.freeSpinsRemaining++;
        const canBud = available.length > 0 && nextState.sticky.length < 9;
        const hasBud = canBud && roll(rng) < V292_TABLES.blueMystery.eventRate;
        cues.push(`blue_mystery:reverse_spin:${nextState.blueMystery.reverseSpins}`, "blue_mystery:reverse");
        if (hasBud) {
            const cell = shuffle(available, rng)[0];
            const source = grid[cell[0]][cell[1]];
            const ordinal = ++nextState.blueMystery.budStops;
            const chance = blueMysteryBloomChance(ordinal, available.length);
            const success = roll(rng) < chance;
            cues.push(`blue_mystery:bud:${ordinal}`, `blue_mystery:chance:${chance}`);
            if (success) {
                // v204: the first actual Mystery bloom is Azure and grants no ordinary FREE spins.
                const entry = { cell, symbol: "azure", spins: 0 };
                grid[cell[0]][cell[1]] = "azure";
                nextState.sticky.push(entry);
                sticky.set(`${cell[0]},${cell[1]}`, entry);
                blooms.push({ cell, source, outcome: "bloomed" });
                cues.push("blue_mystery:release_bloom");
                blueSucceeded = true;
                // v293 conversion happens after this spin's line/ANY settlement, so the newly awakened
                // waiting buds cannot retroactively change this transaction's payout.
                blueReleaseCells = nextState.sticky.filter((item) => item.symbol === "mysticBud");
            }
            else {
                // v198/v209: a failed bud remains a locked waiting cell for a later synchronized Azure release.
                const entry = { cell, symbol: "mysticBud", spins: 0 };
                grid[cell[0]][cell[1]] = "mysticBud";
                nextState.sticky.push(entry);
                sticky.set(`${cell[0]},${cell[1]}`, entry);
                blooms.push({ cell, source, outcome: "sticky" });
                cues.push("blue_mystery:waiting_bud");
            }
        }
    }
    const eligible = available.map(([c, r]) => grid[c][r]).filter(key => NORMAL_KEYS.includes(key));
    let targetRate = nextState.inBonus ? freeBudRate(nextState.freeGrowthLevel || 2, nextState.sticky.length) : V292_TABLES.normalBudTarget;
    if (isSpecial)
        targetRate = nextState.sticky.length >= 9 ? 0 : V292_TABLES.specialBudRate;
    const cueChance = isBlueCollecting || isMysticFinal || isSpecial ? 0 : blueCueChance(targetRate, eligible, nextState.inBonus);
    const hasCue = !isBlueCollecting && !isMysticFinal && !isSpecial && eligible.length > 0 && roll(rng) < cueChance;
    const cue = hasCue ? chooseBlueCue(eligible, rng) : undefined;
    const eventChance = isSpecial ? targetRate : cue ? cue.budChance : nonCueRate(targetRate, cueChance, eligible);
    if (cue)
        cues.push(`blue_cue:${cue.key}`);
    const hasBud = !isBlueCollecting && !isMysticFinal && available.length > 0 && roll(rng) < eventChance;
    if (hasBud) {
        const candidates = shuffle(available, rng);
        let count = 1;
        if (!isSpecial)
            while (count < candidates.length && roll(rng) < V292_TABLES.overlapChance)
                count++;
        for (const cell of candidates.slice(0, count)) {
            const freeCells = 9 - nextState.sticky.length;
            const specialTarget = isSpecial ? specialTargetAt(nextState, cell) : undefined;
            const chance = specialTarget ? 1 : isSpecial ? specialBloomChance(nextState.specialBudStops + 1, freeCells) : nextState.inBonus && (nextState.freeGrowthLevel === 3 || nextState.freeGrowthLevel === 4) ? .33 : .25;
            const source = grid[cell[0]][cell[1]];
            const success = specialTarget ? true : roll(rng) < chance;
            if (isSpecial)
                nextState.specialBudStops++;
            if (!success) {
                blooms.push({ cell, source, outcome: "withered" });
                continue;
            }
            const symbol = specialTarget ? chooseSpecialChanceBloom(specialTarget.tier, rng) : chooseBloom(rng);
            grid[cell[0]][cell[1]] = symbol;
            const existing = sticky.get(`${cell[0]},${cell[1]}`);
            if (!existing) {
                const entry = { cell, symbol, spins: bloomSpins(symbol) };
                nextState.sticky.push(entry);
                sticky.set(`${cell[0]},${cell[1]}`, entry);
            }
            blooms.push({ cell, source, outcome: "bloomed" });
            cues.push(`bloom:${symbol}`);
            if (specialTarget)
                consumeSpecialChanceTarget(nextState, specialTarget, rng, cues);
        }
    }
    maybeRefillSpecialChanceTarget(nextState, rng, cues);
    let payoutLines = evaluateGrid(grid, action);
    if (isMysticFinal) {
        const multiplier = Math.max(V292_TABLES.blueMystery.finalMultiplierAtReverseSpin, nextState.blueMystery.finalMultiplier);
        payoutLines = payoutLines.map((line) => ({ ...line, amount: line.amount * BigInt(multiplier), rule: `${line.rule}_BLUE_X${multiplier}` }));
        cues.push(`blue_mystery:final_multiplier:${multiplier}`);
    }
    const payout = payoutLines.reduce((sum, line) => sum + line.amount, 0n);
    if (isBlueCollecting && blueSucceeded) {
        for (const waiting of blueReleaseCells) {
            waiting.symbol = "azure";
            grid[waiting.cell[0]][waiting.cell[1]] = "azure";
            blooms.push({ cell: waiting.cell, source: "mysticBud", outcome: "released" });
        }
        if (nextState.blueMystery.reverseSpins >= V292_TABLES.blueMystery.finalMultiplierAtReverseSpin) {
            nextState.blueMystery.phase = "final-multiplier";
            nextState.blueMystery.finalMultiplier = nextState.blueMystery.reverseSpins;
            nextState.freeSpinsRemaining = 0;
            cues.push(`blue_mystery:final_pending:${nextState.blueMystery.finalMultiplier}`);
        }
        else
            handBackBlueMystery(nextState, rng, cues);
    }
    else if (isBlueCollecting && nextState.blueMystery.reverseSpins >= V292_TABLES.blueMystery.finalMultiplierAtReverseSpin) {
        // v235: ten accumulated reverse spins are consumed by one final multiplied spin.
        nextState.blueMystery.phase = "final-multiplier";
        nextState.blueMystery.finalMultiplier = nextState.blueMystery.reverseSpins;
        nextState.freeSpinsRemaining = 0;
        cues.push(`blue_mystery:final_pending:${nextState.blueMystery.finalMultiplier}`);
    }
    const newBlooms = blooms.filter(bloom => bloom.outcome === "bloomed");
    if (nextState.inBonus)
        nextState.bonusWin += payout;
    else
        nextState.pendingWin = payout;
    if (!nextState.inBonus && newBlooms.length) {
        nextState.inBonus = true;
        nextState.specialFree = false;
        nextState.specialBudStops = 0;
        nextState.specialChanceTargets = [];
        nextState.specialFirstHitPromotionDone = false;
        nextState.specialRefillCount = 0;
        nextState.specialRefillDone = false;
        nextState.freeGrowthLevel = chooseFreeGrowthLevel(rng);
        const awarded = newBlooms.reduce((sum, bloom) => sum + bloomSpins(grid[bloom.cell[0]][bloom.cell[1]]), 0);
        nextState.freeSpinsRemaining = Math.min(30, awarded);
        nextState.bonusWin += nextState.pendingWin;
        nextState.pendingWin = 0n;
        if (nextState.freeGrowthLevel >= 3 && roll(rng) < V292_TABLES.specialChance.modeChanceAtLevel3Plus) {
            nextState.specialFree = true;
            selectSpecialChanceTargets(nextState, rng, cues);
        }
        cues.push(`free_start:${nextState.freeGrowthLevel}`, `free_award:${awarded}`, ...(nextState.specialFree ? ["chance:free_start"] : []));
    }
    else if (nextState.inBonus && newBlooms.length) {
        const awarded = newBlooms.reduce((sum, bloom) => sum + bloomSpins(grid[bloom.cell[0]][bloom.cell[1]]), 0);
        nextState.freeSpinsRemaining = Math.min(30, nextState.freeSpinsRemaining + awarded);
        cues.push(`free_award:${awarded}`);
    }
    if (isFreeSpin && !isBlueCollecting && !isMysticFinal)
        nextState.freeSpinsRemaining = Math.max(0, nextState.freeSpinsRemaining - 1);
    if (isMysticFinal) {
        nextState.inBonus = false;
        nextState.freeSpinsRemaining = 0;
        nextState.freeGrowthLevel = 0;
        nextState.sticky = [];
        nextState.blueMystery.phase = "spent";
        cues.push("blue_mystery:final_end", "free_end");
    }
    else if (isFreeSpin && nextState.freeSpinsRemaining === 0 && nextState.blueMystery.phase === "idle" && hasAzureSticky(nextState))
        startBlueMystery(nextState, cues);
    else if (isFreeSpin && nextState.freeSpinsRemaining === 0 && !isBlueCollecting) {
        nextState.inBonus = false;
        nextState.freeGrowthLevel = 0;
        nextState.sticky = [];
        nextState.specialChanceTargets = [];
        nextState.specialFirstHitPromotionDone = false;
        nextState.specialRefillCount = 0;
        nextState.specialRefillDone = false;
        cues.push("free_end");
    }
    nextState.revision++;
    const outcome = { nextState, grid, payout, payoutLines, blooms, cues, bet: isFreeSpin ? 0n : currentBet(action), isFreeSpin, resultHashInput: "" };
    outcome.resultHashInput = createResultHashInput(outcome);
    return outcome;
}
function cloneDouble(symbol) { return { ...symbol }; }
export function pickDoubleReelSymbol(rng, excludeKey) { return cloneDouble(pickWeighted(excludeKey ? DOUBLE_REEL_SYMBOLS.filter(symbol => symbol.key !== excludeKey) : DOUBLE_REEL_SYMBOLS, rng)); }
function pickDoubleExcluding(rng, keys) { return cloneDouble(pickWeighted(DOUBLE_REEL_SYMBOLS.filter(symbol => !keys.includes(symbol.key)), rng)); }
export function resolveDoubleSpecialMatch(players) {
    if (players.length !== 3)
        return null;
    const wilds = players.filter(symbol => symbol.wild);
    const nonWild = players.filter(symbol => !symbol.wild);
    if (!wilds.length) {
        const first = players[0];
        return first && !first.blank && first.bonus > 0 && players.every(symbol => symbol.key === first.key) ? { symbol: first, wildSubstitution: false, wildCount: 0 } : null;
    }
    if (!nonWild.length) {
        const flower = wilds[0];
        return flower && flower.bonus > 0 ? { symbol: flower, wildSubstitution: false, wildCount: 3 } : null;
    }
    const target = nonWild[0];
    return target && !target.blank && target.bonus > 0 && nonWild.every(symbol => symbol.key === target.key) ? { symbol: target, wildSubstitution: true, wildCount: wilds.length } : null;
}
export function pickDoublePlayerSymbols(rng) {
    const players = [pickDoubleReelSymbol(rng), pickDoubleReelSymbol(rng), pickDoubleReelSymbol(rng)];
    if (players.every(symbol => symbol.key === players[0].key) && roll(rng) > players[0].matchGate * .45) {
        const index = Math.floor(roll(rng) * 3);
        players[index] = pickDoubleReelSymbol(rng, players[0].key);
    }
    const special = resolveDoubleSpecialMatch(players);
    if (special?.wildSubstitution && roll(rng) > .275) {
        const indexes = players.map((symbol, index) => symbol.wild ? index : -1).filter(index => index >= 0);
        const index = indexes[Math.floor(roll(rng) * indexes.length)];
        players[index] = pickDoubleExcluding(rng, ["flowerLine", special.symbol.key]);
    }
    return players;
}
export function startDouble(state, amount) {
    const source = amount ?? state.pendingWin;
    if (source <= 0n || state.doublePot !== 0n || state.doubleHold !== 0n)
        throw new Error("invalid double start");
    const next = cloneState(state);
    next.doublePot = source;
    next.doubleHold = 0n;
    next.pendingWin = amount === undefined ? 0n : next.pendingWin;
    next.revision++;
    return next;
}
/** Exact v292 full/half -> dealer + three player reels -> selected reel settlement. */
export function resolveDoubleRound(state, mode, selectedReel, rng) {
    if (state.doublePot <= 0n)
        throw new Error("no active double pot");
    if (mode === "half" && state.doublePot < 2n)
        throw new Error("double pot too small for half mode");
    const next = cloneState(state);
    if (mode === "half") {
        const wager = next.doublePot / 2n;
        next.doubleHold += next.doublePot - wager;
        next.doublePot = wager;
    }
    const wager = next.doublePot;
    const dealer = pickDoubleReelSymbol(rng);
    const players = pickDoublePlayerSymbols(rng);
    const chosen = players[selectedReel];
    const special = resolveDoubleSpecialMatch(players);
    const specialBonus = special ? wager * BigInt(special.symbol.bonus) : 0n;
    const base = chosen.rank > dealer.rank ? "won" : chosen.rank === dealer.rank ? "push" : "lost";
    const basePot = base === "won" ? wager * 2n : base === "push" ? wager : 0n;
    next.doublePot = basePot + specialBonus;
    next.revision++;
    const status = base === "lost" && specialBonus === 0n ? "lost" : base === "won" ? "won" : "held";
    return { nextState: next, status, baseOutcome: base === "won" ? "win" : base === "push" ? "push" : "loss", pot: next.doublePot, hold: next.doubleHold, payout: 0n, specialBonus, cues: ["double_dealer", `double_${base}`, ...(special ? [`double_special:${special.symbol.key}`] : [])], round: { dealer, players, selectedReel, wager, mode } };
}
export function collectDouble(state) { if (state.doublePot + state.doubleHold <= 0n)
    throw new Error("no active double pot"); const next = cloneState(state); const payout = next.doublePot + next.doubleHold; next.doublePot = 0n; next.doubleHold = 0n; next.revision++; return { nextState: next, status: "collected", pot: 0n, hold: 0n, payout, cues: ["double_collect"] }; }
/** @deprecated v292 uses resolveDoubleRound(mode, selectedReel), not a binary colour choice. */
export function resolveDoubleAction(state, action, rng) { void rng; if (action !== "collect")
    throw new Error("v292 Double requires mode and selectedReel"); return collectDouble(state); }
/** Stable, BigInt-safe canonical material for a server HMAC/result hash. */
export function createResultHashInput(outcome) {
    return JSON.stringify({ gameId: GAME_ID, gameVersion: GAME_CORE_VERSION, legacyVersion: LEGACY_VERSION, revision: outcome.nextState.revision, bet: outcome.bet.toString(), payout: outcome.payout.toString(), grid: outcome.grid, blooms: outcome.blooms.map(bloom => [bloom.cell[0], bloom.cell[1], bloom.source, bloom.outcome]), lines: outcome.payoutLines.map(line => [line.rule, line.amount.toString()]), state: { inBonus: outcome.nextState.inBonus, freeSpinsRemaining: outcome.nextState.freeSpinsRemaining, freeGrowthLevel: outcome.nextState.freeGrowthLevel, specialFree: outcome.nextState.specialFree, specialBudStops: outcome.nextState.specialBudStops, specialChanceTargets: outcome.nextState.specialChanceTargets.map(item => [item.cell[0], item.cell[1], item.tier]), specialFirstHitPromotionDone: outcome.nextState.specialFirstHitPromotionDone, sticky: outcome.nextState.sticky.map(item => [item.cell[0], item.cell[1], item.symbol, item.spins]), blueMystery: outcome.nextState.blueMystery, pendingWin: outcome.nextState.pendingWin.toString(), bonusWin: outcome.nextState.bonusWin.toString() } });
}
export const wildBloomsCore = Object.freeze({ gameId: GAME_ID, gameVersion: GAME_CORE_VERSION, apiVersion: API_VERSION, productionEnabled: PRODUCTION_ENABLED, resolveSpin, startDouble, resolveDoubleRound, collectDouble });
export const gameCoreRegistry = new Map([[`${GAME_ID}@${GAME_CORE_VERSION}`, wildBloomsCore]]);
//# sourceMappingURL=index.js.map